import React from 'react';
import { View, Text } from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Provider as PaperProvider, MD3LightTheme } from 'react-native-paper';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { Provider as ReduxProvider } from 'react-redux';
import { NavigationContainer } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';

import { AuthProvider, useAuth } from './src/contexts/AuthContext';
import { store } from './src/store';
import SimpleAppNavigator from './src/navigation/SimpleAppNavigator';
import SimpleLoginScreen from './src/screens/SimpleLoginScreen';
import PaywallScreen from './src/screens/PaywallScreen';
import ErrorBoundary from './src/components/ErrorBoundary';
import AppInitializer from './src/components/AppInitializer';
import { ToastProvider } from './src/components/ToastProvider';
import { toast } from './src/services/toastService';

// Log app startup
console.log('[App] Starting Ocean Insight Pro...');
console.log('[App] Environment:', __DEV__ ? 'Development' : 'Production');

// Custom theme
const theme = {
  ...MD3LightTheme,
  colors: {
    ...MD3LightTheme.colors,
    primary: '#0066CC',
    primaryContainer: '#E3F2FD',
    secondary: '#FF7F00',
    secondaryContainer: '#FFF3E0',
    tertiary: '#4CAF50',
    tertiaryContainer: '#E8F5E8',
    surface: '#FFFFFF',
    surfaceVariant: '#F5F5F5',
    background: '#FAFAFA',
    error: '#D32F2F',
    errorContainer: '#FFEBEE',
  },
};

const Stack = createStackNavigator();

function AuthNavigator() {
  const { user, isSubscribed, loading } = useAuth();
  const [loadingTimeout, setLoadingTimeout] = React.useState(false);

  React.useEffect(() => {
    console.log('[AuthNavigator] User state:', user ? 'logged in' : 'not logged in');
    console.log('[AuthNavigator] Subscription state:', isSubscribed ? 'subscribed' : 'not subscribed');
    console.log('[AuthNavigator] Loading state:', loading);
  }, [user, isSubscribed, loading]);

  // Set a timeout for loading state to prevent infinite white screen
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        console.error('[AuthNavigator] Auth loading timeout - forcing render');
        setLoadingTimeout(true);
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeout);
  }, [loading]);

  // Show a visible loading indicator instead of null
  if (loading && !loadingTimeout) {
    console.log('[AuthNavigator] Rendering loading state');
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#FAFAFA' }}>
        <Text style={{ fontSize: 18, color: '#0066CC', marginBottom: 8 }}>Ocean Insight Pro</Text>
        <Text style={{ fontSize: 14, color: '#666' }}>Loading...</Text>
      </View>
    );
  }

  if (loadingTimeout) {
    console.error('[AuthNavigator] Loading timeout reached, showing error');
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#FAFAFA', padding: 20 }}>
        <Text style={{ fontSize: 18, color: '#d32f2f', marginBottom: 8 }}>Loading Error</Text>
        <Text style={{ fontSize: 14, color: '#666', textAlign: 'center' }}>
          Authentication is taking longer than expected. Please check your connection and try reloading.
        </Text>
      </View>
    );
  }

  return (
    <NavigationContainer>
      <Stack.Navigator screenOptions={{ headerShown: false }}>
        {!user ? (
          // Not logged in - show login
          <Stack.Screen name="Login" component={SimpleLoginScreen} />
        ) : !isSubscribed ? (
          // Logged in but not subscribed - show paywall
          <Stack.Screen name="Paywall" component={PaywallScreen} />
        ) : (
          // Logged in and subscribed - show main app
          <Stack.Screen name="Main" component={SimpleAppNavigator} />
        )}
      </Stack.Navigator>
    </NavigationContainer>
  );
}

export default function App() {
  const toastRef = React.useRef(null);

  React.useEffect(() => {
    console.log('[App] App component mounted');
    if (toastRef.current) {
      toast.setToastRef(toastRef.current);
    }
  }, []);

  return (
    <ErrorBoundary>
      <SafeAreaProvider>
        <AuthProvider>
          <ReduxProvider store={store}>
            <AppInitializer>
              <PaperProvider theme={theme}>
                <StatusBar style="light" backgroundColor={theme.colors.primary} />
                <AuthNavigator />
                <ToastProvider ref={toastRef} />
              </PaperProvider>
            </AppInitializer>
          </ReduxProvider>
        </AuthProvider>
      </SafeAreaProvider>
    </ErrorBoundary>
  );
}